/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SystemModule
 */

import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';

import { IIpAddr, IpAddrType } from 'generated-types';

import {
    dnsName,
    ip,
    ipv6,
} from 'ng/utils/regex.utils';

import { L10nService } from '@vmw/ngx-vip';
import { SystemConfiguration } from 'object-types';

import * as l10n from './system-settings-syslog-config.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Component for Syslog configuration in System Settings Modal.
 * @author Hitesh Mandav
 */
@Component({
    selector: 'system-settings-syslog-config',
    templateUrl: './system-settings-syslog-config.component.html',
})
export class SystemSettingsSyslogConfigComponent implements OnInit {
    /**
     * Handle sync syslog to SE checkbox.
     */
    @Input()
    public syncSyslogToSE: boolean;

    @Input()
    public syslogServersList: IIpAddr[];

    /**
     * Eventemitter for sync syslog to SE checkbox.
     */
    @Output()
    public syncSyslogToSEChange = new EventEmitter<boolean>();

    /**
     * Eventemitter for syslog servers list.
     */
    @Output()
    public syslogServersListChange = new EventEmitter<any[]>();

    /**
     * Ip addresses list for syslog servers.
     */
    public syslogServers: string[];

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * object type for template usage.
     */
    public readonly objectType = SystemConfiguration;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Checks if address passed is a IPV4, IPV6 or a hostname.
     */
    private static getIpAddrType(addr: string): IpAddrType {
        if (ip.test(addr)) {
            return IpAddrType.V4;
        } else if (ipv6.test(addr)) {
            return IpAddrType.V6;
        } else if (dnsName.test(addr)) {
            return IpAddrType.DNS;
        }
    }

    /**
     * @override
     */
    public ngOnInit(): void {
        this.syslogServers = this.syslogServersList?.map(syslogServer => syslogServer.addr);
    }

    /**
     * Syslog servers list is updated as user updates syslog servers.
     */
    public onSyslogServersUpdate(syslogServerList: string[] = []): void {
        this.syslogServersList = syslogServerList.reduce(
            (syslogServerList: IIpAddr[], dnsServer: string) => {
                const dnsList = dnsServer.split(/[, ]+/);

                const serverList = dnsList.reduce((serverList: IIpAddr[], addr: string) => {
                    if (addr) {
                        const type = SystemSettingsSyslogConfigComponent.getIpAddrType(addr);

                        serverList.push({
                            addr,
                            type,
                        });
                    }

                    return serverList;
                }, []);

                return syslogServerList.concat(serverList);
            }, [],
        );

        this.syslogServers = this.syslogServersList?.map(syslogServer => syslogServer.addr);
        this.syslogServersListChange.emit(this.syslogServersList);
    }

    /**
     * Update and emit sync syslog to SE checkbox state
     */
    public onSyncSyslogToSEChanged(updatedSyncSyslogToSEState: boolean): void {
        this.syncSyslogToSEChange.emit(updatedSyncSyslogToSEState);
    }
}
